import React, { useRef, useEffect } from "react";

interface LoadingSpinnerProps {
  loading?: boolean;
  onComplete?: () => void;
}

// onComplete is intended to fire after the animation has finished.
export default function AnimatedLoadingSpinner({
  loading = true,
  onComplete,
}: LoadingSpinnerProps) {
  // Internal loading is used to delay the transition from loading to complete state so
  // that the animation can smoothly transition from the loading state to the complete state without "jumping".
  const [internalLoading, setInternalLoading] = React.useState(loading);

  const animationRef = useRef<SVGAnimateTransformElement>(null);
  const checkmarkAnimationRef = useRef<SVGAnimateElement>(null);

  useEffect(() => {
    const animation = animationRef.current;

    if (loading && !internalLoading) {
      setInternalLoading(true);
    }

    if (animation) {
      const handleAnimationIteration = () => {
        if (!loading) {
          setInternalLoading(false);
        }
      };

      animation.addEventListener("repeatEvent", handleAnimationIteration);

      return () => {
        animation.removeEventListener("repeatEvent", handleAnimationIteration);
      };
    }
  }, [loading, internalLoading]);

  // Listen for completion animation finish
  useEffect(() => {
    const checkmarkAnimation = checkmarkAnimationRef.current;

    if (checkmarkAnimation && !internalLoading && onComplete) {
      const handleAnimationEnd = () => {
        onComplete();
      };

      checkmarkAnimation.addEventListener("endEvent", handleAnimationEnd);

      return () => {
        checkmarkAnimation.removeEventListener("endEvent", handleAnimationEnd);
      };
    }
  }, [internalLoading, onComplete]);

  return (
    <div className="flex items-center justify-center">
      <svg
        key={internalLoading ? "loading" : "complete"}
        width="120px"
        height="120px"
        viewBox="0 0 256 256"
        xmlns="http://www.w3.org/2000/svg"
      >
        {internalLoading ? (
          /* Loading State - Quarter Circle Spinner */
          <circle
            cx="128"
            cy="128"
            r="96"
            fill="none"
            stroke="currentColor"
            strokeWidth="6"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeDasharray="151 453"
            transform="rotate(-90 128 128)"
          >
            <animateTransform
              ref={animationRef}
              attributeName="transform"
              attributeType="XML"
              type="rotate"
              dur="750ms"
              from="-90 128 128"
              to="270 128 128"
              repeatCount="indefinite"
            />
          </circle>
        ) : (
          <>
            {/* Completion State - Full Circle */}
            <circle
              cx="128"
              cy="128"
              r="96"
              fill="none"
              stroke="currentColor"
              strokeWidth="6"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDasharray="603"
              strokeDashoffset="452"
              transform="rotate(-90 128 128)"
            >
              <animate
                attributeName="stroke-dashoffset"
                values="452;0"
                dur="400ms"
                fill="freeze"
              />
            </circle>

            {/* Checkmark */}
            <path
              d="M88 128l24 24 56-56"
              fill="none"
              stroke="currentColor"
              strokeWidth="6"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDasharray="120"
              strokeDashoffset="120"
              opacity="0"
            >
              <animate
                ref={checkmarkAnimationRef}
                attributeName="stroke-dashoffset"
                values="120;0"
                dur="250ms"
                begin="200ms"
                fill="freeze"
              />
              <animate
                attributeName="opacity"
                values="0;1"
                dur="20ms"
                begin="200ms"
                fill="freeze"
              />
            </path>
          </>
        )}
      </svg>
    </div>
  );
}
